/**
 * A workspace's own settings, as rows — and the one line this whole lot was
 * built to deliver.
 *
 * Until now this was a `textarea` holding every key as JSON, which is the
 * editor an organization's settings stopped being. The rows are the same ones:
 * `org-catalogue.ts` is read from here rather than copied, so a label, a hint,
 * a bound or an option exists once and a member finds the same words on their
 * machine, on their organization's page and here.
 *
 * Two things a workspace's row does that an organization's does not:
 *
 * - **`null` is inheritance, not "unset".** A row nobody has answered shows
 *   what the layer above says — the organization where it has an opinion, the
 *   application otherwise — and says which layer that was, on the row, beside
 *   the control.
 *
 *   The control is live from the start all the same, as it is on the
 *   organization's page: *Override* was a click in front of every change whose
 *   only effect was to let somebody touch the field they were already looking
 *   at, and a field showing the inherited value **is** the offer to override
 *   it. Changing it writes the override; *Reset* gives the row back. The
 *   argument for keeping the click here was that a workspace answers three
 *   settings out of twenty-seven, so one that overrides nothing would look like
 *   one that overrides everything — which is true of a page where a row says
 *   nothing about where its value came from, and not of this one, because the
 *   tag is on every row this workspace has not answered and on none that it
 *   has. The dot in the gutter went with the verb, for the same reason: it
 *   marked, one column to the left, what the tag already says.
 * - **It says when an override is doing nothing.** `enforced` freezes a setting
 *   across every install, so an override written under a lock is kept, stored,
 *   published — and applies to nothing. That used to be discoverable only by
 *   noticing a session had the wrong memory. Now it is said on the row that
 *   causes it, at the moment it is written, by somebody who does not yet
 *   suspect anything.
 *
 * And the value is **kept rather than refused**. The organization may unlock the
 * setting tomorrow, and 32768 is what this workspace wants: held, marked amber,
 * said to be dormant. Refusing the input would throw away a legitimate
 * preference to enforce a temporary rule.
 */
import type React from 'react';
import { DEFAULT_SETTINGS } from '@airlock/contracts/settings/defaults';
import { color, font } from '../../theme/tokens';
import {
  InheritedValue,
  LockedValue,
  SettingNumber,
  SettingRow,
  SettingSelect,
  SettingSet,
  SettingText,
  SettingToggle,
  SourceTag,
  Verb,
  type SettingMark
} from '../../components/settings-row';
import type { DocConfig } from '../../lib/types';
import { DockerfileField } from './DockerfileField';
import { ExtensionsField } from './ExtensionsField';
import { AutoLandField } from './AutoLandField';
import { AuditField } from './AuditField';
import {
  MODEL_EFFORT_OPTIONS,
  isDocumentRow,
  pairOf,
  splitPair,
  type SettingSpec,
  type SettingsSectionSpec
} from './org-catalogue';
import type { HostService } from '@airlock/contracts/settings/hostservice';
import { MAX_PRIVACY_TERM_CHARS, MAX_PRIVACY_TERMS, privacyTerms } from '@airlock/contracts/settings/primitives';
import { DomainList, HostServiceList, ListBlock } from './list-settings';
import { GitFormatsField } from './git-formats-settings';
import type { GitFormats } from '@airlock/contracts/settings/git-formats';
import { LocalServiceList } from './LocalServiceList';
import type { LocalService } from '@airlock/contracts/settings/localservice';
import type { DocProject } from '@airlock/contracts/workspace/document';
import { isPersonalKey } from '@airlock/contracts/workspace/personal';
import { showValue, type Values } from './setting-values';
import { emptyConfig } from './shared';

/** What the app itself answers, under every other layer. */
const APP: Values = DEFAULT_SETTINGS as unknown as Values;

/**
 * The keys a workspace's document can carry, read off the validator's own
 * shape — plus the override it leaves out of an empty one because it is
 * written only when answered (`SparseOverride`).
 */
const OVERRIDABLE: ReadonlySet<string> = new Set([...Object.keys(emptyConfig()), 'autoLand', 'audit']);

/**
 * The tag on an inherited value when nobody above this workspace has an opinion
 * — and the sentence it cannot fit.
 *
 * It read `APP`, over the value `DEFAULT_SETTINGS` holds, which was taken for
 * what every member's sessions run with. They do not: an unanswered row is not
 * in the document at all, so each machine keeps **its own** application
 * setting, and what is shown here is only what Airlock ships. An administrator
 * read `Opus · X-High` here and found Haiku on the machine that joined, which
 * is two true statements about two different layers.
 */
const FROM_APP = 'AIRLOCK';
const APP_MEANS =
  'Airlock’s own default. This row is not in the published document, so every member’s machine keeps whatever their application settings say — which may not be this.';

/**
 * The organization's layer, as this page needs to read it.
 *
 * `version` is in here because the warning names it: "locked when it published
 * v15" is what turns an unexplained value into something an administrator can
 * be asked about.
 */
export interface OrgLayer {
  name: string;
  version: number;
  defaults: Values;
  enforced: readonly string[];
}

/** Whether the organization has frozen a key — a lock with no value is not one. */
function lockedAbove(org: OrgLayer | null, key: string): boolean {
  return Boolean(org && org.enforced.includes(key) && org.defaults[key] !== undefined && org.defaults[key] !== null);
}

/** A layer's answered keys only: `null` in one of these means "I say nothing". */
function definedOnly(layer: Values): Values {
  return Object.fromEntries(Object.entries(layer).filter(([, value]) => value !== undefined && value !== null));
}

/** A value quoted as the document spells it. */
function Mono({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <span style={{ fontFamily: font.mono }}>{children}</span>;
}

/** What applies to this workspace when it says nothing, and which layer says it. */
function inherited(org: OrgLayer | null, key: string): { from: string; value: unknown } {
  const above = org?.defaults[key];
  return above === undefined || above === null ? { from: FROM_APP, value: APP[key] } : { from: org?.name ?? FROM_APP, value: above };
}

/**
 * The two integration switches a workspace overrides.
 *
 * They are settings in the document and integrations on the rail, and this is
 * where the two facts meet: the organization decides them on the GitHub and
 * ngrok pages, so a workspace that wants a different answer says it beside the
 * boards it reads, not among its sandbox sizes. The labels and hints are the
 * organization's own, minus the half about locking — a workspace enforces
 * nothing on anybody.
 */
export const WORKSPACE_INTEGRATION_SPEC: SettingsSectionSpec = {
  key: 'wsIntegrations',
  lede: 'What this workspace uses of the accounts the organization has connected.',
  groups: [
    {
      rows: [
        {
          key: 'gitEnabled',
          label: 'GitHub',
          kind: 'bool',
          hint: 'Start a session from a pull request, and push its merge back onto that pull request’s branch. Off means nothing is downloaded and no provider token is read for this workspace.'
        },
        {
          key: 'tunnelsEnabled',
          label: 'Public URLs',
          kind: 'bool',
          hint: 'Lets a session in this workspace publish a port at a public address. Off by default, unlike Preview: this one reaches out of the machine.'
        }
      ]
    }
  ]
};

/** Why a name cannot be masked as typed — the app's own clamp (`privacyTerms`) is the judge. */
function termProblem(entry: string, listed: readonly string[]): string | null {
  const kept = privacyTerms([entry]) ?? [];
  if (kept[0] !== entry) return `At least two characters and at most ${MAX_PRIVACY_TERM_CHARS}, with single spaces.`;
  const twin = listed.find((one) => one.toLowerCase() === entry.toLowerCase());
  return twin ? `${twin} is already here — names are matched in any case.` : null;
}

/** The rows of a section this workspace can actually answer for, groups kept. */
function groupsOf(spec: SettingsSectionSpec): { label?: string; rows: SettingSpec[] }[] {
  return spec.groups
    .map((group) => ({ label: group.label, rows: group.rows.filter((row) => OVERRIDABLE.has(row.key)) }))
    .filter((group) => group.rows.length > 0);
}

export function WorkspaceSettings({
  spec,
  config,
  org,
  editable,
  repos = []
}: {
  spec: SettingsSectionSpec;
  config: DocConfig;
  /** The workspace's projects, which a local service's `${project/VARIABLE}` names (§5.9.1). */
  repos?: readonly DocProject[];
  /** What the organization says over the application, `null` while it is unread. */
  org: OrgLayer | null;
  /** How the rows write back. Absent for somebody who may only read. */
  editable?: (patch: Partial<DocConfig>) => void;
}): React.JSX.Element {
  const held = config as unknown as Values;
  const put = (patch: Values): void => editable?.(patch as Partial<DocConfig>);

  // Two readings of every row, and the warning needs both. `fromAbove` is what
  // this workspace would get if it said nothing; `applied` is what a session
  // here actually starts with — which is also what the conditional rows are
  // read against, since a Docker disk size means nothing where the *effective*
  // runtime is not Docker, whichever layer answered it.
  const fromAbove: Values = { ...APP, ...definedOnly(org?.defaults ?? {}) };
  const applied: Values = { ...fromAbove };
  for (const [one, value] of Object.entries(held)) {
    if (value !== null && value !== undefined && !lockedAbove(org, one) && !isPersonalKey(one)) applied[one] = value;
  }

  const row = (rowSpec: SettingSpec): React.JSX.Element => {
    const key = rowSpec.key;
    // The model and the effort are one row over two keys, so overriding and
    // resetting both act on the pair — a workspace that pinned a model and
    // inherited its effort could name a combination no session can be launched
    // with, which is the refusal `contracts/settings/models.ts` makes.
    const keys = rowSpec.kind === 'modelEffort' ? [key, 'defaultEffort'] : [key];
    // A personal setting is in no version (1080/personal-settings-stay), so this
    // page has nothing to offer on it, and what an older document holds is read by
    // nobody. Only the organization's lock imposes one, and that row says so.
    const personal = isPersonalKey(key);
    const own = !personal && keys.some((one) => held[one] !== undefined && held[one] !== null);
    const above = inherited(org, key);
    const frozen = lockedAbove(org, key);
    const hidden = !personal && rowSpec.needs && applied[rowSpec.needs.key] !== rowSpec.needs.value;
    const mark: SettingMark | undefined = own && frozen ? 'dead' : undefined;

    const reset = (): void => put(Object.fromEntries(keys.map((one) => [one, null])));


    // What the control opens on: this workspace's answer where it has one, and
    // otherwise what it is inheriting — so the field is the offer to override,
    // and changing it *is* the override. An answered row under a lock keeps
    // showing what it holds rather than the value overruling it: that is what
    // the warning underneath is about, and a row that quietly redrew itself as
    // the organization's number would leave nothing to explain.
    const source: Values = own ? held : fromAbove;

    const control = ((): React.ReactNode => {
      const value = source[key];
      if (rowSpec.kind === 'bool') return <SettingToggle on={Boolean(value)} onToggle={() => put({ [key]: !value })} />;
      if (rowSpec.kind === 'agents') {
        return (
          <SettingSet
            value={Array.isArray(value) ? (value as string[]) : []}
            options={rowSpec.options ?? []}
            onChange={(next) => put({ [key]: next })}
          />
        );
      }
      if (rowSpec.kind === 'oneOf') {
        return (
          <SettingSelect
            value={String(value ?? '')}
            options={rowSpec.options ?? []}
            onChange={(next) => put({ [key]: next })}
            dead={frozen}
          />
        );
      }
      if (rowSpec.kind === 'modelEffort') {
        return (
          <SettingSelect
            value={pairOf(String(source.defaultModel ?? ''), source.defaultEffort ? String(source.defaultEffort) : null)}
            options={MODEL_EFFORT_OPTIONS}
            onChange={(pair) => put(splitPair(pair) as Values)}
            dead={frozen}
          />
        );
      }
      if (rowSpec.kind === 'int') {
        const number = rowSpec.scale ? Math.round(Number(value) / rowSpec.scale) : Number(value);
        return (
          <SettingNumber
            value={Number.isFinite(number) ? number : (rowSpec.min ?? 0)}
            min={rowSpec.min ?? 0}
            max={rowSpec.max ?? 0}
            step={rowSpec.step}
            unit={rowSpec.unit}
            onChange={(next) => put({ [key]: rowSpec.scale ? next * rowSpec.scale : next })}
            dead={frozen}
          />
        );
      }
      return (
        <SettingText
          value={String(value ?? '')}
          placeholder={rowSpec.placeholder}
          onChange={(next) => put({ [key]: next })}
          dead={frozen}
        />
      );
    })();

    // A locked setting this workspace has *not* overridden keeps no control:
    // there is nothing left to decide, and a live field there would be an
    // offer to write a value that is dormant before it is typed.
    //
    // Nor does a reader who cannot write get one. The live control is the
    // offer to answer, and offering it to somebody whose change would go
    // nowhere is the same lie as the field behind *Override* used to be —
    // they get the value and the layer it came from, which is what they came
    // to read.
    const value = frozen && !own ? (
      <LockedValue by={org?.name ?? 'the organization'}>{showValue(rowSpec, fromAbove)}</LockedValue>
    ) : personal ? (
      <span style={{ fontSize: 13, color: color.inkSubtle }}>Each member keeps their own</span>
    ) : !own && !editable ? (
      <InheritedValue from={above.from} title={above.from === FROM_APP ? APP_MEANS : undefined}>
        {showValue(rowSpec, fromAbove)}
      </InheritedValue>
    ) : isDocumentRow(rowSpec.kind) ? null : (
      // Live, on this workspace's answer or on the inherited value; which of
      // the two it is, and from which layer, is said under the hint (§11.2).
      control
    );

    // Where the value comes from, under the hint (§11.2, §46.7): « Inherited
    // from » and the layer's tag on a row still borrowing, « Overrides » with
    // the value it replaces on a row answered here, « Locked by » on a row the
    // organization holds shut. A reader's value already names its layer.
    const tag = <SourceTag title={above.from === FROM_APP ? APP_MEANS : undefined}>{above.from}</SourceTag>;
    const from =
      frozen && !own ? (
        <>
          Locked by <SourceTag>{org?.name ?? 'the organization'}</SourceTag>
        </>
      ) : own && !frozen ? (
        <>
          Overrides {tag}
          <Mono>{showValue(rowSpec, fromAbove)}</Mono>
        </>
      ) : !own && editable && !personal ? (
        <>Inherited from {tag}</>
      ) : undefined;

    return (
      <SettingRow
        key={key}
        name={rowSpec.label}
        settingKey={rowSpec.kind === 'modelEffort' ? 'defaultModel · defaultEffort' : key}
        hint={rowSpec.hint}
        wide={isDocumentRow(rowSpec.kind) && !(frozen && !own) && Boolean(editable || own)}
        depends={hidden ? rowSpec.needs?.note : undefined}
        mark={mark}
        value={value}
        source={from}
        warn={
          own && frozen ? (
            <>
              This is not applying. {org?.name} locked this setting when it published v{org?.version}. Sessions in this
              workspace run with <Mono>{showValue(rowSpec, fromAbove)}</Mono>, not <Mono>{showValue(rowSpec, held)}</Mono>.
            </>
          ) : undefined
        }
        // Only where there is something to give back. A row still inheriting
        // has nothing to reset, and the verb column stays empty on it — which
        // is now the difference the reader can act on, as it is on the
        // organization's page.
        action={
          editable && own ? (
            <Verb
              onClick={reset}
              title={frozen ? 'Drop the value that is not applying.' : 'Give this setting back to the layer above.'}
            >
              Reset
            </Verb>
          ) : undefined
        }
      >
        {/* The two documents, each drawn by its own editor: there is no value
            column on either, and what sits under the line *is* the value. */}
        {rowSpec.kind === 'wide' && !(frozen && !own) && (editable || own) && (
          <DockerfileField
            value={String(source.dockerfile ?? '')}
            locked={!editable}
            onChange={(next) => put({ dockerfile: next })}
          />
        )}
        {rowSpec.kind === 'extensions' && !(frozen && !own) && (editable || own) && (
          <ExtensionsField
            value={source.agentExtensions as never}
            editable={!!editable}
            onChange={(next) => put({ agentExtensions: next as never })}
          />
        )}
        {rowSpec.kind === 'autoLand' && !(frozen && !own) && (editable || own) && (
          <AutoLandField value={source.autoLand} editable={!!editable} onChange={(next) => put({ autoLand: next })} />
        )}
        {rowSpec.kind === 'audit' && !(frozen && !own) && (editable || own) && (
          <AuditField value={source.audit} editable={!!editable} onChange={(next) => put({ audit: next })} />
        )}
      </SettingRow>
    );
  };

  const groups = groupsOf(spec);

  /**
   * The three lists, under the section they belong to.
   *
   * Not rows of the catalogue: the catalogue is shared with the organization's
   * page, and these are the settings an organization deliberately does not
   * answer for (`list-settings.tsx` says why). They are the workspace's own,
   * with nothing above them to inherit — so no source tag, and no Reset.
   */
  const lists =
    spec.key === 'network' ? (
      <>
        <ListBlock
          label="Hosts this workspace adds"
          hint="Added to what each member's own Airlock already allows — a workspace widens the allowlist for itself and never narrows it."
        >
          <DomainList
            value={(held.extraDomains as string[] | undefined) ?? []}
            onChange={(next) => put({ extraDomains: next })}
            editable={!!editable}
            placeholder="api.stripe.com"
          />
        </ListBlock>
        <ListBlock
          label="Hosts this workspace refuses"
          hint="Asked about nothing and allowed by nothing: a blocklist only narrows, so there is no layer above it to inherit from."
        >
          <DomainList
            value={(held.blockedDomains as string[] | undefined) ?? []}
            onChange={(next) => put({ blockedDomains: next })}
            editable={!!editable}
            placeholder="telemetry.example.com"
          />
        </ListBlock>
        <ListBlock
          label="Services on the member's own machine"
          hint="Each one opens a single address and a single port from a session to the machine running it (§5.9) — the one deliberate hole in the boundary, which is why it is per workspace and never an organization's default. The variables are injected into every project."
        >
          <HostServiceList
            value={(held.hostServices as HostService[] | undefined) ?? []}
            onChange={(next) => put({ hostServices: next })}
            editable={!!editable}
          />
        </ListBlock>
        <ListBlock
          label="Containers on each member's machine"
          hint="Images each member's Airlock offers to pull and run with their own container runner — Docker Desktop, OrbStack, Colima, Podman — after they join, and never without their click. Ports are published on their machine; volumes are named, never a folder of it; a secret is a reference to a project's variable, ${project/VARIABLE}, never a value in this document."
        >
          <LocalServiceList
            value={(held.localServices as LocalService[] | undefined) ?? []}
            repos={repos}
            onChange={(next) => put({ localServices: next })}
            editable={!!editable}
          />
        </ListBlock>
      </>
    ) : spec.key === 'all' ? (
      <ListBlock
        label="Names this workspace masks"
        hint="Clients and people of these repositories, masked as [TERM_…] while the mask is on — added to the names each member lists on their own machine, never instead of them. Matched as a whole word, in any case."
      >
        <DomainList
          value={(held.privacyTerms as string[] | undefined) ?? []}
          onChange={(next) => put({ privacyTerms: next })}
          editable={!!editable}
          placeholder="Acme Corp"
          problemOf={(entry) => termProblem(entry, (held.privacyTerms as string[] | undefined) ?? [])}
          max={MAX_PRIVACY_TERMS}
        />
      </ListBlock>
    ) : spec.key === 'git' ? (
      <ListBlock
        label="Branch and commit formats"
        hint="How this workspace's branches are named and its commit subjects written (§5.4). Members' agents are told it and name a merge's branch and write its message in it; the app flags a name off the format, never refuses it. The workspace's own, like the lists: an organization does not default it."
      >
        <GitFormatsField
          value={(held.gitFormats as GitFormats | null | undefined) ?? null}
          onChange={(next) => put({ gitFormats: next })}
          editable={!!editable}
        />
      </ListBlock>
    ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {groups.map((group, index) => (
        <div key={group.label ?? index}>
          {group.label && (
            <div
              style={{
                paddingTop: index === 0 ? 0 : 20,
                paddingBottom: 4,
                fontFamily: font.mono,
                fontSize: 10.5,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: color.inkFaint
              }}
            >
              {group.label}
            </div>
          )}
          {group.rows.map(row)}
        </div>
      ))}
      {lists}
    </div>
  );
}
